<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import SiteVisitCard from './SiteVisitCard.vue';
import SiteVisitForm from './SiteVisitForm.vue';
import SiteVisitDetailsDrawer from './SiteVisitDetailsDrawer.vue';
import nexceoProjectOptionsAPI from 'dashboard/api/nexceo/projectOptions';

const store = useStore();

const visits = computed(() => store.getters['nexceoSiteVisits/allSiteVisits']);
const uiFlags = computed(() => store.getters['nexceoSiteVisits/getUIFlags']);
const agents = computed(() => store.getters['agents/getAgents']);
const projects = ref([]);
const loadError = ref('');

// View mode: 'month' | 'list'
const viewMode = ref('month');

// Current month navigation
const today = new Date();
const viewYear = ref(today.getFullYear());
const viewMonth = ref(today.getMonth()); // 0-indexed

const monthLabel = computed(() =>
  new Date(viewYear.value, viewMonth.value, 1).toLocaleString([], {
    month: 'long',
    year: 'numeric',
  })
);

function prevMonth() {
  if (viewMonth.value === 0) {
    viewYear.value -= 1;
    viewMonth.value = 11;
  } else viewMonth.value -= 1;
}
function nextMonth() {
  if (viewMonth.value === 11) {
    viewYear.value += 1;
    viewMonth.value = 0;
  } else viewMonth.value += 1;
}
function goToday() {
  viewYear.value = today.getFullYear();
  viewMonth.value = today.getMonth();
}

// Filters
const filterProject = ref('');
const filterAgent = ref('');
const filterStatus = ref('');

const STATUSES = [
  'scheduled',
  'confirmed',
  'rescheduled',
  'completed',
  'cancelled',
  'no_show',
];
const STATUS_LABELS = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  rescheduled: 'Rescheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No Show',
};

function toDateKey(d) {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const filteredVisits = computed(() => {
  let list = visits.value;
  if (filterProject.value)
    list = list.filter(
      v => String(v.project_id) === String(filterProject.value)
    );
  if (filterAgent.value)
    list = list.filter(
      v => String(v.assigned_agent_id) === String(filterAgent.value)
    );
  if (filterStatus.value)
    list = list.filter(v => v.status === filterStatus.value);
  return list;
});

// Calendar grid generation
const calendarDays = computed(() => {
  const year = viewYear.value;
  const month = viewMonth.value;
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Start from Sunday of the week containing the 1st
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - startDate.getDay());

  // End on Saturday of the week containing the last day
  const endDate = new Date(lastDay);
  endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

  const days = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    const key = toDateKey(cursor);
    // M26.15.8 — scheduled_at_ist_date (backend-computed, IST-anchored) is
    // used instead of slicing the raw UTC scheduled_at string, which was
    // unsafe for any visit within a few hours of the UTC/IST midnight
    // boundary (e.g. a 00:30 IST visit is 19:00 UTC the previous day, so
    // slicing put it on the wrong grid day).
    const dayVisits = filteredVisits.value.filter(
      v => v.scheduled_at_ist_date === key
    );
    days.push({
      date: new Date(cursor),
      key,
      dayNum: cursor.getDate(),
      isCurrentMonth: cursor.getMonth() === month,
      isToday: key === toDateKey(today),
      visits: dayVisits,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
});

// List view groups
const groupedByDate = computed(() => {
  const groups = {};
  filteredVisits.value.forEach(v => {
    const key = v.scheduled_at_ist_date || 'unknown';
    if (!groups[key]) groups[key] = [];
    groups[key].push(v);
  });
  return Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateKey, dayVisits]) => ({
      dateKey,
      label:
        dateKey === 'unknown'
          ? '—'
          : new Date(`${dateKey}T00:00:00`).toLocaleDateString([], {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            }),
      isToday: dateKey === toDateKey(today),
      visits: dayVisits,
    }));
});

const hasVisits = computed(() => groupedByDate.value.length > 0);

async function load() {
  loadError.value = '';
  const from = new Date(viewYear.value, viewMonth.value, 1).toISOString();
  const to = new Date(
    viewYear.value,
    viewMonth.value + 1,
    0,
    23,
    59,
    59
  ).toISOString();
  try {
    await store.dispatch('nexceoSiteVisits/fetchSiteVisits', { from, to });
  } catch {
    loadError.value = 'Failed to load visits.';
  }
}

watch([viewYear, viewMonth], load);

onMounted(async () => {
  load();
  if (!agents.value.length) store.dispatch('agents/get');
  try {
    const { data } = await nexceoProjectOptionsAPI.get();
    projects.value = data?.projects ?? [];
  } catch {
    /* non-fatal */
  }
});

const isFormOpen = ref(false);
const selectedVisit = ref(null);
const isDrawerOpen = ref(false);

function openDrawer(visit) {
  selectedVisit.value = visit;
  isDrawerOpen.value = true;
}

// Reschedule-audit fix: `selectedVisit` was a one-time snapshot taken at
// openDrawer() time, including for this drawer's OWN reschedule/confirm/
// complete/cancel actions (onVisitUpdated only reloads the list + leads, it
// never repointed selectedVisit) — so the open drawer kept showing the
// pre-reschedule scheduled_at until closed and reopened. Watching the store
// record directly keeps it live for both this drawer's own actions and any
// externally-triggered update (other tab/agent, websocket refresh).
watch(
  () =>
    selectedVisit.value &&
    store.getters['nexceoSiteVisits/getSiteVisitById'](selectedVisit.value.id),
  updated => {
    if (updated) selectedVisit.value = updated;
  }
);
function onVisitSaved() {
  load();
  store.dispatch('nexceoLeads/fetchLeads');
}
function onVisitUpdated() {
  load();
  store.dispatch('nexceoLeads/fetchLeads');
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Page Header -->
    <div
      class="flex items-center gap-3 px-4 h-14 bg-n-background border-b border-n-weak shrink-0"
    >
      <span class="i-lucide-calendar-days size-4 text-woot-500 shrink-0" />
      <h1 class="text-base font-semibold text-n-slate-12">
        Site Visit Calendar
      </h1>

      <div class="flex-1" />

      <!-- Month nav -->
      <div class="flex items-center gap-1">
        <button
          class="flex items-center justify-center w-7 h-7 rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-1 transition-colors"
          @click="prevMonth"
        >
          <span class="i-lucide-chevron-left size-4" />
        </button>
        <span
          class="text-sm font-semibold text-n-slate-12 min-w-[110px] sm:min-w-[130px] text-center"
        >
          {{ monthLabel }}
        </span>
        <button
          class="flex items-center justify-center w-7 h-7 rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-1 transition-colors"
          @click="nextMonth"
        >
          <span class="i-lucide-chevron-right size-4" />
        </button>
        <button
          class="hidden sm:block ml-1 px-2.5 py-1 rounded-lg text-xs font-medium border border-n-weak text-n-slate-9 hover:bg-n-alpha-1 transition-colors"
          @click="goToday"
        >
          Today
        </button>
      </div>

      <!-- View toggle -->
      <div class="flex items-center gap-0.5 bg-n-alpha-1 rounded-lg p-0.5">
        <button
          class="flex items-center justify-center w-7 h-7 rounded-md transition-colors"
          :class="
            viewMode === 'month'
              ? 'bg-n-background shadow-sm text-n-slate-12'
              : 'text-n-slate-8 hover:text-n-slate-12'
          "
          title="Month view"
          @click="viewMode = 'month'"
        >
          <span class="i-lucide-grid-3x3 size-3.5" />
        </button>
        <button
          class="flex items-center justify-center w-7 h-7 rounded-md transition-colors"
          :class="
            viewMode === 'list'
              ? 'bg-n-background shadow-sm text-n-slate-12'
              : 'text-n-slate-8 hover:text-n-slate-12'
          "
          title="List view"
          @click="viewMode = 'list'"
        >
          <span class="i-lucide-list size-3.5" />
        </button>
      </div>

      <!-- New Visit -->
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 bg-woot-500 text-white rounded-lg text-sm font-medium hover:bg-woot-600 transition-colors shrink-0"
        @click="isFormOpen = true"
      >
        <span class="i-lucide-plus size-4" />
        <span class="hidden sm:inline">New Visit</span>
      </button>
    </div>

    <!-- Filter row -->
    <div
      class="flex items-center gap-2 px-4 py-2 bg-n-background border-b border-n-weak shrink-0 flex-wrap gap-y-2"
    >
      <div class="relative">
        <select
          v-model="filterProject"
          class="appearance-none text-sm border border-n-weak rounded-lg pl-3 pr-7 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 cursor-pointer"
        >
          <option value="">All Projects</option>
          <option v-for="p in projects" :key="p.id" :value="p.id">
            {{ p.name }}
          </option>
        </select>
        <span
          class="i-lucide-chevron-down size-3 text-n-slate-8 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
        />
      </div>

      <div class="relative">
        <select
          v-model="filterStatus"
          class="appearance-none text-sm border border-n-weak rounded-lg pl-3 pr-7 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 cursor-pointer"
        >
          <option value="">All Statuses</option>
          <option v-for="s in STATUSES" :key="s" :value="s">
            {{ STATUS_LABELS[s] }}
          </option>
        </select>
        <span
          class="i-lucide-chevron-down size-3 text-n-slate-8 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
        />
      </div>

      <div class="relative">
        <select
          v-model="filterAgent"
          class="appearance-none text-sm border border-n-weak rounded-lg pl-3 pr-7 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 cursor-pointer"
        >
          <option value="">All Agents</option>
          <option v-for="a in agents" :key="a.id" :value="a.id">
            {{ a.name }}
          </option>
        </select>
        <span
          class="i-lucide-chevron-down size-3 text-n-slate-8 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
        />
      </div>

      <button
        v-if="filterProject || filterStatus || filterAgent"
        class="text-xs text-ruby-600 hover:text-ruby-700 font-medium px-2 py-1.5 rounded-lg hover:bg-ruby-50 transition-colors"
        @click="
          filterProject = '';
          filterStatus = '';
          filterAgent = '';
        "
      >
        Clear
      </button>

      <div class="flex-1" />

      <!-- Today button on mobile (was hidden in header) -->
      <button
        class="sm:hidden px-2.5 py-1 rounded-lg text-xs font-medium border border-n-weak text-n-slate-9 hover:bg-n-alpha-1 transition-colors"
        @click="goToday"
      >
        Today
      </button>
    </div>

    <!-- Error banner -->
    <div
      v-if="loadError"
      class="flex items-center gap-2 px-4 py-2 bg-ruby-50 dark:bg-ruby-500/10 border-b border-ruby-200 text-sm text-ruby-700 shrink-0"
    >
      <span class="i-lucide-alert-circle size-4 shrink-0" />
      {{ loadError }}
      <button class="underline ml-1 font-medium" @click="load">Retry</button>
    </div>

    <!-- Loading -->
    <div
      v-if="uiFlags.isFetching"
      class="flex-1 flex items-center justify-center"
    >
      <div class="flex flex-col items-center gap-3 text-n-slate-7">
        <span class="i-lucide-loader-2 size-7 animate-spin text-woot-500" />
        <span class="text-sm">Loading visits</span>
      </div>
    </div>

    <!-- Month Calendar Grid -->
    <div v-else-if="viewMode === 'month'" class="flex-1 overflow-auto min-h-0">
      <!-- Day headers -->
      <div
        class="grid grid-cols-7 border-b border-n-weak bg-n-background sticky top-0 z-10"
      >
        <div
          v-for="day in DAYS_OF_WEEK"
          :key="day"
          class="py-2 text-center text-xs font-semibold text-n-slate-9 uppercase tracking-wide"
        >
          {{ day }}
        </div>
      </div>

      <!-- Calendar grid -->
      <div class="grid grid-cols-7 flex-1">
        <div
          v-for="day in calendarDays"
          :key="day.key"
          class="min-h-[100px] border-b border-r border-n-weak p-1.5 transition-colors"
          :class="[
            !day.isCurrentMonth
              ? 'bg-n-alpha-1 dark:bg-n-solid-1'
              : 'bg-n-background',
            day.isToday ? 'ring-1 ring-inset ring-woot-500' : '',
          ]"
        >
          <!-- Day number -->
          <div class="flex items-center justify-between mb-1">
            <span
              class="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold"
              :class="[
                day.isToday
                  ? 'bg-woot-500 text-white'
                  : day.isCurrentMonth
                    ? 'text-n-slate-12'
                    : 'text-n-slate-6',
              ]"
            >
              {{ day.dayNum }}
            </span>
            <span
              v-if="day.visits.length > 0"
              class="text-[10px] font-semibold text-woot-600 dark:text-woot-400"
            >
              {{ day.visits.length }}
            </span>
          </div>

          <!-- Visit chips -->
          <div class="space-y-0.5">
            <button
              v-for="visit in day.visits.slice(0, 3)"
              :key="visit.id"
              class="w-full text-left flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium hover:opacity-80 transition-opacity"
              :class="[
                visit.status === 'confirmed'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  : visit.status === 'completed'
                    ? 'bg-n-alpha-2 text-n-slate-9'
                    : visit.status === 'cancelled'
                      ? 'bg-ruby-50 text-ruby-700 line-through dark:bg-ruby-900/20'
                      : visit.status === 'rescheduled'
                        ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/30'
                        : 'bg-woot-50 text-woot-700 dark:bg-woot-900/20 dark:text-woot-400',
              ]"
              :title="visit.lead?.name || `Lead #${visit.lead_id}`"
              @click="openDrawer(visit)"
            >
              <span
                v-if="visit.visit_type === 'video_call'"
                class="i-lucide-video size-2.5 shrink-0"
                title="Video Call"
              />
              <span class="shrink-0">{{ visit.scheduled_at_ist_time }}</span>
              <span class="truncate">{{
                visit.lead?.name || `Lead #${visit.lead_id}`
              }}</span>
            </button>
            <button
              v-if="day.visits.length > 3"
              class="text-[10px] text-n-slate-8 pl-1 cursor-pointer underline hover:text-n-slate-11 transition-colors"
              @click="viewMode = 'list'"
            >
              +{{ day.visits.length - 3 }} more
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- List View -->
    <div
      v-else-if="viewMode === 'list'"
      class="flex-1 overflow-y-auto px-4 py-4"
    >
      <!-- Loading handled above -->
      <div
        v-if="!hasVisits"
        class="flex flex-col items-center justify-center h-60 gap-4"
      >
        <div
          class="flex items-center justify-center w-14 h-14 rounded-2xl bg-woot-50 dark:bg-woot-500/10"
        >
          <span class="i-lucide-calendar-x size-7 text-woot-500" />
        </div>
        <div class="text-center">
          <p class="text-base font-semibold text-n-slate-12">
            No visits scheduled for {{ monthLabel }}
          </p>
          <p class="text-sm text-n-slate-9 mt-1">Use 'New Visit' to add one.</p>
        </div>
        <button
          class="flex items-center gap-2 px-4 py-2 bg-woot-500 text-white rounded-lg text-sm font-medium hover:bg-woot-600 transition-colors"
          @click="isFormOpen = true"
        >
          <span class="i-lucide-plus size-4" />
          New Visit
        </button>
      </div>

      <div v-else class="flex flex-col gap-6 max-w-3xl">
        <div v-for="group in groupedByDate" :key="group.dateKey">
          <div class="flex items-center gap-3 mb-2.5">
            <div
              class="flex items-center justify-center w-8 h-8 rounded-xl text-sm font-bold shrink-0"
              :class="[
                group.isToday
                  ? 'bg-woot-500 text-white'
                  : 'bg-n-alpha-2 dark:bg-n-solid-3 text-n-slate-10',
              ]"
            >
              {{
                group.dateKey !== 'unknown'
                  ? new Date(`${group.dateKey}T00:00:00`).getDate()
                  : '?'
              }}
            </div>
            <div class="flex items-center gap-2">
              <p
                class="text-sm font-semibold"
                :class="group.isToday ? 'text-woot-500' : 'text-n-slate-12'"
              >
                {{ group.label }}
              </p>
              <span
                v-if="group.isToday"
                class="px-2 py-0.5 text-xs bg-woot-50 text-woot-600 dark:bg-woot-500/10 dark:text-woot-400 rounded-full font-medium"
              >
                Today
              </span>
            </div>
            <div class="flex-1 h-px bg-n-weak dark:bg-n-strong" />
            <span class="text-xs text-n-slate-8 shrink-0">
              {{ group.visits.length }}
              {{ group.visits.length !== 1 ? 'visits' : 'visit' }}
            </span>
          </div>

          <div class="flex flex-col gap-2 pl-11">
            <SiteVisitCard
              v-for="visit in group.visits"
              :key="visit.id"
              :visit="visit"
              @click="openDrawer"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <SiteVisitForm
    :is-open="isFormOpen"
    @close="isFormOpen = false"
    @saved="onVisitSaved"
  />

  <SiteVisitDetailsDrawer
    :visit="selectedVisit"
    :is-open="isDrawerOpen"
    @close="isDrawerOpen = false"
    @updated="onVisitUpdated"
  />
</template>
